import { useInputControl } from '@conform-to/react'
import React, { type ComponentProps, useId, useRef, useState } from 'react'
import { cn } from '#app/utils/misc.js'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from './ui/command.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import {
	Select,
	SelectContent,
	SelectTrigger,
	SelectValue,
} from './ui/select.tsx'
import { Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map((e) => (
				<li
					key={e}
					className="text-[11px] font-semibold text-foreground-destructive"
				>
					{e}
				</li>
			))}
		</ul>
	)
}

export const CommandField = ({
	labelProps,
	inputProps,
	errors,
	options,
	onChange,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: ComponentProps<typeof CommandInput>
	onChange: (value: string) => void
	errors?: ListOfErrors
	options: string[]
}) => {
	const [isFocused, setIsFocused] = useState(true)
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	delete inputProps.key
	const ref = useRef<HTMLInputElement>(null)

	return (
		<div className="relative">
			<Label {...labelProps} />
			<div>
				<Command loop className="relative overflow-visible">
					<CommandInput
						{...inputProps}
						ref={ref}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setTimeout(() => setIsFocused(false), 500)}
						name={inputProps.name}
						value={inputProps.value || ''}
						onValueChange={(value) => onChange(value)}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
					/>
					<CommandList
						className={cn(
							'absolute top-full z-10 w-full bg-white shadow-md',
							!!options.length && !!inputProps.value && isFocused
								? ''
								: 'hidden',
						)}
					>
						<CommandGroup>
							{options.map((x) => (
								<CommandItem
									key={x}
									value={x}
									onSelect={() => {
										onChange(x)
										ref.current?.blur()
									}}
								>
									{x}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</div>
			<div className="min-h-[20px] px-4 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			<div className="min-h-[20px] px-4 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectField({
	labelProps,
	selectProps,
	triggerProps,
	selectValueProps,
	errors,
	className,
	children,
}: {
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	selectProps: ComponentProps<typeof Select> & { id: string }
	triggerProps?: ComponentProps<typeof SelectTrigger>
	selectValueProps?: ComponentProps<typeof SelectValue>
	errors?: ListOfErrors
	className?: string
	children: React.ReactNode
}) {
	const fallbackId = useId()
	const id = selectProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Select {...selectProps}>
				<SelectTrigger
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					{...triggerProps}
				>
					<SelectValue {...selectValueProps} />
				</SelectTrigger>
				<SelectContent>{children}</SelectContent>
			</Select>
			<div className="min-h-[20px] px-4 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="self-center text-body-xs text-muted-foreground"
				/>
			</div>
			<div className="px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
